/** biome-ignore-all lint/suspicious/noExplicitAny: processRichTextPlaceholders */

import {getDateTimeFormatter} from '@gfiber/typesmith';
import {z} from 'zod';
import {
  backEndEgressUrls,
  backEndSvcGrpcUrls,
  backEndSvcUrls,
  type Env,
  type EnvMap,
  GfiberVanityUrlsMap,
  GoogleVanityUrlsMap,
  GRACEFUL_EXCEPTION_LOBBY_ACCOUNT_ID,
  LOBBY_ACCOUNT_ID_LENGTH,
  type VanityDomain,
  type VanitySlug,
} from './constants';
/**
 * Get back-end SVC url string based on current env
 * based on current env using the run-time env variable TARGET_ENV
 */
export function getBackendSvcUrl() {
  if (process.env.BACKEND_SVC_URL !== undefined) {
    return process.env.BACKEND_SVC_URL;
  }
  return _getEnvMappedString(backEndSvcUrls);
}

export function getBackendSvcGrpcUrl() {
  return _getEnvMappedString(backEndSvcGrpcUrls);
}

export function getBackendEgressUrl() {
  return _getEnvMappedString(backEndEgressUrls);
}

/**
 * get string for gfiber/fiber/apiGateway/backendSvc/backendSvcGrpc/etc.
 * based on current env using the run-time env variable TARGET_ENV
 */
export function _getEnvMappedString(map: EnvMap): string {
  const env = (process.env.NEXT_PUBLIC_TARGET_ENV ?? 'local') as Env;
  if (env in map) {
    return map[env];
  }
  throw new Error(`NEXT_PUBLIC_TARGET_ENV=${env} is not valid`);
}

export function replaceMultipleInJsonString(
  jsonObject: any,
  replacements: {[key: string]: string},
): any {
  let jsonString = JSON.stringify(jsonObject);
  for (const placeholderName in replacements) {
    if (Object.hasOwn(replacements, placeholderName)) {
      const replacementValue = replacements[placeholderName];
      const fullPlaceholderToFind = `\${${placeholderName}}`;
      const escapedPlaceholder = fullPlaceholderToFind.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&',
      );
      const regex = new RegExp(escapedPlaceholder, 'g');
      jsonString = jsonString.replace(regex, replacementValue);
    }
  }
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    console.error(
      'CRITICAL ERROR: JSON parsing failed after string replacement. This means your replacement corrupted the JSON structure.',
    );
    console.error(
      'Attempted replacement string (might be invalid JSON):',
      jsonString,
    );
    throw new Error(
      `JSON corruption detected. Cannot parse result. Original error: ${(e as Error).message}`,
    );
  }
}

export function getVanityUrl(vanityDomain: VanityDomain, slug: VanitySlug) {
  const env = (process.env.NEXT_PUBLIC_TARGET_ENV ?? 'prod') as Env;
  const vanityMap =
    vanityDomain === 'gfiber'
      ? GfiberVanityUrlsMap[slug]
      : GoogleVanityUrlsMap[slug];
  const linkUrl = vanityMap[env];
  if (!linkUrl) {
    throw new Error(
      `NEXT_PUBLIC_TARGET_ENV=${env} is not valid for vanity slug "${slug}"`,
    );
  }
  const replacements: {[key: string]: string} = {
    'https://': '',
    'http://': '',
    '//': '',
    'www.': '',
  };
  const regexPattern = Object.keys(replacements).join('|');
  const regex = new RegExp(regexPattern, 'gi');
  return {
    linkUrl,
    linkText: linkUrl.replace(
      regex,
      match => replacements[match.toLowerCase()],
    ),
  };
}
// Function to recursively traverse the contentful document and replace text
export function insertLinktoDocument(
  node: any,
  stringToReplace: string,
  replaceLink: {linkUrl: string; linkText: string},
) {
  if (node.nodeType === 'text' && node.value.includes(stringToReplace)) {
    // Split the text value at the replacement point
    const parts = node.value.split(stringToReplace);

    // Create new nodes for the parts before and after the link
    const newContent = [];

    // Add the part before the link
    if (parts[0]) {
      newContent.push({
        nodeType: 'text',
        value: parts[0],
        marks: node.marks,
        data: {},
      });
    }

    // Create the new hyperlink node
    newContent.push({
      nodeType: 'hyperlink',
      data: {
        uri: replaceLink.linkUrl,
      },
      content: [
        {
          nodeType: 'text',
          value: replaceLink.linkText,
          marks: [{type: 'italic'}], // Optional: add marks like italic or bold
          data: {},
        },
      ],
    });

    // Add the part after the link
    if (parts[1]) {
      newContent.push({
        nodeType: 'text',
        value: parts[1],
        marks: node.marks,
        data: {},
      });
    }

    return newContent;
  }

  // If the node has 'content', recurse into it
  if (node.content) {
    const newContent = [];
    for (const child of node.content) {
      const result = insertLinktoDocument(child, stringToReplace, replaceLink);
      if (Array.isArray(result)) {
        newContent.push(...result);
      } else {
        newContent.push(result);
      }
    }
    node.content = newContent;
  }

  return node;
}

type getTimeInfoType = {
  etrIsToday: boolean;
  timeString: string;
};
function formatDateTimeInfo(
  endDate: Date,
  now: Date,
  zone?: string,
  locale = 'en',
) {
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  };
  if (zone) timeOptions.timeZone = zone;

  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  if (zone) dateOptions.timeZone = zone;

  const timeFormatter = getDateTimeFormatter(locale, timeOptions);
  const dateFormatter = getDateTimeFormatter(locale, dateOptions);

  return {
    timeString: timeFormatter.format(endDate),
    etrIsToday: dateFormatter.format(endDate) === dateFormatter.format(now),
  };
}

export function getTimeInfo(
  endDateTime: string,
  timezone: string | undefined,
  locale = 'en',
): getTimeInfoType {
  const etrDate = new Date(endDateTime);
  const endSeconds = etrDate.getTime() / 1000;
  const timeDiff = endSeconds - Date.now() / 1000;
  const timeInfo = {
    etrIsToday: false,
    timeString: '',
  };

  if (86400 > timeDiff && timeDiff > 0) {
    const zone = timezone || undefined;
    const endDate = new Date(endSeconds * 1000);
    const now = new Date();

    try {
      return {...timeInfo, ...formatDateTimeInfo(endDate, now, zone, locale)};
    } catch {
      return {
        ...timeInfo,
        ...formatDateTimeInfo(endDate, now, undefined, locale),
      };
    }
  }

  return timeInfo;
}

export function joinNonEmptyStrings(
  stringArr: (string | undefined | null)[],
  delimiter: string,
  isEndWithDelimiter = false,
) {
  const combinedStr = stringArr.filter(v => !!v).join(delimiter);
  return isEndWithDelimiter && combinedStr?.length
    ? `${combinedStr}${delimiter}`
    : combinedStr;
}

/**
 * NextResponse builder
 */
export const buildResponseOptions = (traceId: string, status: number) => {
  return {
    status: status,
    headers: {
      'X-Api-Request-Id': traceId,
    },
  };
};

/**
 * Determines if an Account ID should be encrypted based on the environment
 * and a specific configuration value.
 *  */
export const isAccountIdEncrypted = (
  encryptionConfigValue: string | boolean | null,
) => {
  const env = process.env.NEXT_PUBLIC_TARGET_ENV;
  //Staging and Production environments always considered as Encrypted Value.
  if (env === 'staging' || env === 'prod') {
    return true;
  }
  //All other environments
  // Check for string 'true' OR boolean true to auto-handle all intended truthy types.
  // Null, 'false', false, and undefined will all resolve to false here.
  const isConfigTrue =
    encryptionConfigValue === 'true' || encryptionConfigValue === true;
  return isConfigTrue;
};
interface LobbyAccountValidationFields {
  accountId: string;
  isEncrypted: boolean;
}
/**
 * Safely extracts the accountId and isEncrypted flag from either a nested
 * (GET route) or a flat (POST body) object structure.
 */
function extractValidationFields(
  data: any,
): LobbyAccountValidationFields | null {
  if (data?.params && data?.searchParams) {
    return {
      accountId: data.params.accountId as string,
      isEncrypted: !!data.searchParams.isEncrypted,
    };
  }
  if (
    data &&
    typeof data.accountId === 'string' &&
    typeof data.isEncrypted === 'boolean'
  ) {
    return {
      accountId: data.accountId,
      isEncrypted: data.isEncrypted,
    };
  }
  return null;
}

/**
 * Applies conditional length validation for 'accountId' based on 'isEncrypted'.
 * The core logic is defined here once and reused across all schemas.
 * @param schema The base Zod schema to apply the refinement to.
 */
export function applyAccountIdRefinement<T extends z.ZodType>(schema: T) {
  return schema.refine(
    data => {
      const fields = extractValidationFields(data);
      if (!fields) {
        return false;
      }
      const {accountId, isEncrypted} = fields;
      const len = accountId.length;
      if (isEncrypted) {
        return (
          len >= LOBBY_ACCOUNT_ID_LENGTH.ENCRYPTED.MIN &&
          len <= LOBBY_ACCOUNT_ID_LENGTH.ENCRYPTED.MAX
        );
      }
      if (accountId === GRACEFUL_EXCEPTION_LOBBY_ACCOUNT_ID) {
        return true;
      }
      return (
        len >= LOBBY_ACCOUNT_ID_LENGTH.RAW.MIN &&
        len <= LOBBY_ACCOUNT_ID_LENGTH.RAW.MAX
      );
    },
    {
      message:
        'Invalid Account Id! Length does not match encrypted/raw constraints.',
      path: ['accountId'],
    },
  );
}

export function isRequired(field: z.ZodTypeAny): boolean {
  return !(field instanceof z.ZodOptional);
}

interface ZodLengthCheck {
  kind?: string;
  value?: number;
  _zod?: {
    def?: {
      check?: string;
      maximum?: number;
    };
  };
}

interface ZodSchemaWithChecks {
  def?: {checks?: ZodLengthCheck[]};
  _def?: {checks?: ZodLengthCheck[]};
}

/**
 * Extracts the `max` constraint from a Zod schema as a number, or undefined if
 * not present. This function is brittle, as it relies on Zod internal data
 * structures which might change in future versions.
 *
 * @param field The Zod schema from which to extract max constraint.
 */
export function maxLength(field: z.ZodTypeAny): number | undefined {
  const baseField = field instanceof z.ZodOptional ? field.unwrap() : field;

  const schema = baseField as unknown as ZodSchemaWithChecks;
  const checks = schema.def?.checks || schema._def?.checks;

  const max = checks?.find(
    check => check._zod?.def?.check === 'max_length' || check.kind === 'max',
  );

  if (max) {
    return max._zod?.def?.maximum ?? max.value;
  }

  return undefined;
}
